"use client";

import React, { useState, useEffect } from "react";
import { proxyStorageUrl } from "@/utils/storage/proxyStorageUrl";

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt: string;
  size?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fallbackClassName?: string;
  role?: string;
  userType?: string;
  fallbackText?: string;
}

/**
 * Avatar component that displays a user's profile image
 * and falls back to initials when the image fails to load.
 * When a role or userType is provided, the fallback avatar will use
 * the role's color scheme for visual consistency.
 */
const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size = 40,
  className = "",
  fallbackClassName = "",
  role,
  userType,
  fallbackText,
  ...props
}) => {
  // Set initial state to true if no src is provided
  // This ensures fallback initials appear immediately
  const [imgError, setImgError] = useState(true);
  
  // Map string sizes to pixel values
  const sizeMap: Record<string, number> = {
    'xs': 24,
    'sm': 32,
    'md': 40,
    'lg': 48,
    'xl': 64,
  };

  const pixelSize = typeof size === 'number' ? size : (sizeMap[size] || 40);

  // Rewrite local Supabase storage URLs to proxied paths for remote access (ngrok)
  const proxiedSrc = proxyStorageUrl(src) ?? undefined;

  // Handle image loading on client-side
  useEffect(() => {
    if (!proxiedSrc) return; // No need to check if no src provided

    // Create an image to test loading
    const img = new window.Image();
    img.src = proxiedSrc;
    
    // If image loads successfully, update state
    img.onload = () => setImgError(false);
    img.onerror = () => setImgError(true);
    
    // Initial check for cached images
    if (img.complete) {
      setImgError(false);
    }
  }, [proxiedSrc]);

  // Generate initials from name
  const getInitials = (name: string) => {
    if (fallbackText) return fallbackText;
    
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Get color class based on user role to match role badge colors
  const getRoleColorClass = (roleName?: string) => {
    const activeRole = roleName || userType;
    if (!activeRole) return "bg-gray-100 text-gray-600";
    
    switch (activeRole) {
      case 'gym_owner':
      case 'owner':
        return 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400';
      case 'coach':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400';
      case 'parent':
        return 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400';
      case 'athlete':
        return 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  // Determine background color based on name (fallback if no role provided)
  const getColorClass = (name: string) => {
    const colorClasses = [
      "bg-blue-100 text-blue-600",
      "bg-green-100 text-green-600",
      "bg-yellow-100 text-yellow-600",
      "bg-red-100 text-red-600",
      "bg-purple-100 text-purple-600",
      "bg-pink-100 text-pink-600",
      "bg-indigo-100 text-indigo-600",
    ];
    
    // Simple hash function to pick a color based on name
    const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colorClasses[hash % colorClasses.length];
  };

  const baseClasses = `rounded-full overflow-hidden flex items-center justify-center ${className}`;
  const initials = getInitials(alt);
  // Use role-based color if available, otherwise fall back to name-based color
  const colorClass = (role || userType) ? getRoleColorClass(role || userType) : getColorClass(alt);
  
  // If there's no image or it failed to load, show initials
  if (!proxiedSrc || imgError) {
    // Determine text size based on avatar size
    let textSizeClass = "text-sm";
    if (pixelSize >= 100) {
      textSizeClass = "text-4xl";
    } else if (pixelSize >= 80) {
      textSizeClass = "text-3xl";
    } else if (pixelSize >= 60) {
      textSizeClass = "text-2xl";
    } else if (pixelSize >= 40) {
      textSizeClass = "text-base";
    } else if (pixelSize < 28) {
      textSizeClass = "text-[10px]";
    }

    return (
      <div 
        className={`${baseClasses} ${colorClass} ${fallbackClassName}`}
        style={{ width: pixelSize, height: pixelSize }}
        {...props}
      >
        <span className={`${textSizeClass} font-bold leading-none ${fallbackClassName}`}>{initials}</span>
      </div>
    );
  }

  // Otherwise show the image
  return (
    <div className={baseClasses} style={{ width: pixelSize, height: pixelSize }} {...props}>
      <img
        src={proxiedSrc}
        alt={alt}
        className="w-full h-full object-cover"
        onError={() => setImgError(true)}
      />
    </div>
  );
};

export default Avatar; 